import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, Observable, of, switchMap, tap, map } from 'rxjs';
import { ApiUser, DriverCard, DailyDispatchParams, Orders, AssignOrder, LoadStatus, Trailers, AssignMiscOrder, Drivers, Trucks, Customers, CustomerCreate, CustomerResponse } from '../interfaces';
import { DateTime } from 'luxon';
import { SocketService } from './socket.service';
import { environment } from '../../environments/environment';



@Injectable({
  providedIn: 'root'
})

export class DataServiceService {
  

  constructor(private http: HttpClient,
    private socketService: SocketService
  ) { 
    // // Commented out socket service initialization to bypass data acquisition
    // /*
    this.socketService.initializeEntity('driver', this.driversSubject);
    this.socketService.initializeEntity('truck', this.trucksSubject);
    this.socketService.initializeEntity('order', this.ordersSubject);
    this.socketService.initializeEntity('trailer', this.trailersSubject);
    this.socketService.initializeEntity('dispatch', this.driverCardsSubject);
    this.socketService.initializeEntity('customer', this.customersSubject);
    this.socketService.initializeEntity('dispatch_order', this.dispatchOrdersSubject);
    
    // Pass the driverCardsSubject to the socket service
    this.socketService.setDriverCardsSubject(this.driverCardsSubject);
    
    // Set this service in the socket service
    this.socketService.setDataService(this);
    
    this.refreshOrders();
    
    
    // Initialize with empty data for UI display
    this.driverCardsSubject.next([]);
    this.driversSubject.next([]);
    this.ordersSubject.next([]);
    this.trucksSubject.next([]);
    this.trailersSubject.next([]);
    this.customersSubject.next([]);
    this.dispatchOrdersSubject.next([]);
  }

  getDriverSubject() {
    return this.driversSubject;
  }

  private apiUrl = environment.apiUrl;
  // private apiUrl = 'http://localhost:8000';


  private currentUser = new BehaviorSubject<ApiUser | null>(null);
  currentUser$ = this.currentUser.asObservable();

  private driverCardsSubject = new BehaviorSubject<DriverCard[]>([]);
  public driverCards$ = this.driverCardsSubject.asObservable();

  private driversSubject = new BehaviorSubject<Drivers[]>([]);
  drivers$ = this.driversSubject.asObservable();

  private selectedDateSubject = new BehaviorSubject<string>(
    DateTime.now()
      .setZone('America/Chicago')
      .toFormat('yyyy-MM-dd')
  );
  selectedDate$ = this.selectedDateSubject.asObservable();

  private ordersSubject = new BehaviorSubject<Orders[]>([]);
  public orders$ = this.ordersSubject.asObservable();

  private trucksSubject = new BehaviorSubject<Trucks[]>([]);
  public trucks$ = this.trucksSubject.asObservable();

  private trailersSubject = new BehaviorSubject<Trailers[]>([]);
  public trailers$ = this.trailersSubject.asObservable();

  private customersSubject = new BehaviorSubject<Customers[]>([]);
  public customers$ = this.customersSubject.asObservable();

  // Add a new subject for dispatch orders
  private dispatchOrdersSubject = new BehaviorSubject<any[]>([]);
  public dispatchOrders$ = this.dispatchOrdersSubject.asObservable();

  /** Synchronously retrieves the current user */
  getCurrentUser(): ApiUser | null {
    return this.currentUser.value;
  }

  /* Gets driver cards and filters */
  getDailyDispatch(
    date: string,
    params: DailyDispatchParams = {}
  ): Observable<DriverCard[]> {
    let httpParams = new HttpParams();
    // Add date parameter
    httpParams = httpParams.set('date', date);
    // Add optional parameters if they exist
    if (params.driver_first_name) {
      httpParams = httpParams.set('driver_first_name', params.driver_first_name);
    }
    if (params.driver_last_name) {
      httpParams = httpParams.set('driver_last_name', params.driver_last_name);
    }
    if (params.pickup_number) {
      httpParams = httpParams.set('pickup_number', params.pickup_number);
    }
    return this.http.get<DriverCard[]>(`${this.apiUrl}/daily-dispatch`, { params: httpParams }).pipe(
      catchError((error) => {
        console.error('Error fetching daily dispatch:', error);
        return of([]); // Return an empty array as fallback
      })
    );
  }

  /* Updates Daily Dispatch (notes and driver status) */
  updateDriverDailyDispatch(
    dispatchId: number,
    driverId: number,
    updates: { notes?: string; daily_driver_status?: string }
  ): Observable<any> {
    return this.http.patch(
      `${this.apiUrl}/drivers/${driverId}/dispatches/${dispatchId}`,
      updates
    ).pipe(
      tap(updatedDispatch => {
        const currentCards = this.driverCardsSubject.getValue();
        const updatedCards = currentCards.map(card => 
          card.dailyDispatch.id === dispatchId ? 
          {
            ...card,
            dailyDispatch: {
              ...card.dailyDispatch,
              ...updates,
              id: dispatchId
            }
          } : card
        );
        
        this.driverCardsSubject.next(updatedCards);
      })
    );
  }

  /* Adds an order to a driver's dispatch orders - (used when add unassigned load or moving an order from one driver to another) */
  assignOrderToDriverDispatch(
    driverId: number,
    dispatchId: number,
    orderData: AssignOrder,
  ): Observable<any> {
    const url = `${this.apiUrl}/drivers/${driverId}/dispatches/${dispatchId}/dispatch_orders`;
    return this.http.post(url, orderData).pipe(
      switchMap(response => {
        // Update the order with new driver IDs and status
        return this.updateOrder(orderData.order_id, {
          ...orderData,
          load_status: 'Scheduled',
          pickup_driver_id: driverId,   // Sets both pickup and delivery driver IDs to the same driver
          delivery_driver_id: driverId
        });
      }),
      tap(() => {
        this.refreshDriverCards();
        this.refreshOrders();
      })
    );
  }

  /* Handles a split load assignment, when assigning to either pickup or delivery driver */
  assignSplitOrderToDispatch(
    driverId: number,
    dispatchId: number,
    orderData: AssignOrder,
  ): Observable<any> {
    const url = `${this.apiUrl}/drivers/${driverId}/dispatches/${dispatchId}/split_order`;
    return this.http.post(url, orderData).pipe(
      tap(() => {
        this.refreshDriverCards();
        this.refreshOrders();
      })
    );
  }

  /* Remove split order assignment */
  removeSplitOrderFromDispatch(
    driverId: number,
    dispatchId: number,
    orderId: number
  ): Observable<any> {
    const url = `${this.apiUrl}/drivers/${driverId}/dispatches/${dispatchId}/split_order/${orderId}`;
    return this.http.delete(url).pipe(
      tap(() => {
        this.refreshDriverCards();
        this.refreshOrders();
      }),
      catchError((error) => {
        console.error('Error removing split order:', error);
        return of(null); // Return null as fallback
      })
    );
  }


  /* Adds a miscellaneous task to a driver's dispatch orders */
  assignMiscTask(
    driverId: number,
    dispatchId: number,
    taskData: AssignMiscOrder
  ): Observable<any> {
    const url = `${this.apiUrl}/drivers/${driverId}/dispatches/${dispatchId}/misc_task`;

    return this.http.post(url, taskData).pipe(
      tap({
        next: (response) => {
          this.refreshDriverCards();
        },
        error: (error) => console.error('Misc task assignment failed:', error)
      })
    );
  }

  /* Updates an existing miscellaneous task on a driver's dispatch */
  updateMiscTask(
    driverId: number,
    dispatchId: number,
    taskId: number,
    taskData: Partial<AssignMiscOrder>
  ): Observable<any> {
    const url = `${this.apiUrl}/drivers/${driverId}/dispatches/${dispatchId}/misc_task/${taskId}`;

    return this.http.patch(url, taskData).pipe(
      tap({
        next: () => {
          this.refreshDriverCards();
        },
        error: (error) => console.error('Misc task update failed:', error)
      }),
      catchError((error) => {
        console.error('Error updating misc task:', error);
        return of(null);
      })
    );
  }

  /* Deletes miscellaneous task from driver dispatch */
  removeMiscTaskFromDriverDispatch(
    driverId: number,
    dispatchId: number,
    taskId: number
  ): Observable<any> {
    const url = `${this.apiUrl}/drivers/${driverId}/dispatches/${dispatchId}/misc_task/${taskId}`;

    return this.http.delete(url).pipe(
      tap(() => {
        this.refreshDriverCards();
      }),
      catchError((error) => {
        console.error('Error removing misc task:', error);
        return of(null); // Return null as fallback
      })
    );
  }

  /* Creates a new order */
  createOrder(orderData: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/orders`, orderData).pipe(
      tap(() => {
        this.refreshOrders();
      })
    );
  }

  /* Gets all orders (queries date if necessary) */
  getOrders(date?: string, orderId?: number, customerId?: number): Observable<Orders[]> {
    let params = new HttpParams();
    if (date) {
      const centralDate = new Date(date);
      params = params.set('date', centralDate.toLocaleString());
    }
    if (orderId) {
      params = params.set('order_id', orderId.toString());
    }
    if (customerId) {
      params = params.set('customer_id', customerId.toString());
    }
    
    return this.http.get<Orders[]>(`${this.apiUrl}/orders`, { params }).pipe(
      tap(orders => {
        this.ordersSubject.next(orders);
      })
    );
  }

  /* Updates an existing order */
  updateOrder(orderId: number, updatedData: Partial<Orders>): Observable<any> {
    return this.http.put(`${this.apiUrl}/orders/${orderId}`, updatedData).pipe(
      tap(() => {
        console.log('Order updated, refreshing orders');
        this.refreshOrders();
      })
    );
  }


  /* Delete order from all orders - not just from a dispatch */
  deleteOrder(orderId: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/orders/${orderId}`).pipe(
      tap(() => {
        this.refreshOrders();
      })
    );
  }


  /* Removes an order from a driver's dispatch orders */
  removeOrderFromDriverDispatch(
    driverId: number,
    dispatchId: number,
    orderId: number,
    loadStatus?: LoadStatus
  ): Observable<any> {
    const url = `${this.apiUrl}/drivers/${driverId}/dispatches/${dispatchId}/dispatch_orders/${orderId}`;

    return this.http.delete(url).pipe(
      // Use switchMap to chain the order update
      switchMap(() => {
        // Only update status if we're removing completely, not for reassignment
        if (loadStatus) {
          return this.updateOrder(orderId, {
            load_status: loadStatus,
            pickup_driver_id: null,
            delivery_driver_id: null
          });
        }
        // If no loadStatus, just return the delete result
        return of(null);
      }),
      // After all operations complete, refresh data
      tap(() => {
        this.refreshDriverCards();
        this.refreshOrders();
      })
    );
  }

  /* Retrieves a list of all drivers */
  getAllDrivers(): Observable<any> {
    return this.http.get(`${this.apiUrl}/drivers`)
  }

  /* Add new driver */
  createDriver(driver: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/drivers`, driver).pipe(
      tap(newDriver => {
        // Get current drivers
        const currentDrivers = this.driversSubject.getValue();
        // Format the new driver with name
        const formattedDriver = {
          ...newDriver,
          name: `${newDriver.first_name} ${newDriver.last_name}`
        };
        // Add new driver to array
        this.driversSubject.next([...currentDrivers, formattedDriver]);
        
        // Emit the update through socket service
        this.socketService.handleEntityUpdate({
          type: 'driver',
          data: [...currentDrivers, formattedDriver]
        });
      })
    );
  }


  /* Remove driver (admin only) */
  removeDriver(driverId: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/drivers/${driverId}`).pipe(
      tap(() => {
        // Get current drivers
        const currentDrivers = this.driversSubject.getValue();
        // Remove driver from array
        const updatedDrivers = currentDrivers.filter(driver => driver.driver_id !== driverId);
        this.driversSubject.next(updatedDrivers);
      })
    );
  }


  /* Retrieves list of all trailers */
  getAllTrailers(): Observable<any> {
    return this.http.get(`${this.apiUrl}/trailers`).pipe(
        tap(trailers => {
            this.trailersSubject.next(trailers as Trailers[]); // Update the trailers subject with the new data
        })
    );
  }

  /* Add new trailer */
  createTrailer(trailerData: any): Observable<any> {
    return this.http.post<Trailers>(`${this.apiUrl}/trailers`, trailerData).pipe(
      tap(() => {
        this.getAllTrailers().subscribe();
      })
    );
  }



  /* Remove a trailer */
  removeTrailer(trailerId: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/trailers/${trailerId}`).pipe(
      tap(() => {
        const currentTrailers = this.trailersSubject.getValue();
        this.trailersSubject.next(currentTrailers.filter(trailer => trailer.trailer_id !== trailerId));
      })
    );
  }


  /* Update a trailer (status/availability/set up?) */
  updateTrailer(trailerId: number, updatedData: Partial<Trailers>): Observable<any> {
    console.log('Updating trailer:', trailerId, updatedData);
    return this.http.put(`${this.apiUrl}/trailers/${trailerId}`, updatedData).pipe(
      switchMap(updatedTrailer => {
        // First get the location name if we're updating location_id
        if (updatedData.customer_id) {
          return this.getAllCustomers().pipe(
            map(customers => {
              const customer = customers.find((c: any) => c.customer_id === updatedData.customer_id);
              return {
                ...updatedTrailer,
                customer_name: customer?.name || ''
              };
            })
          );
        }
        return of(updatedTrailer);
      }),
      tap(updatedTrailer => {
        const currentTrailers = this.trailersSubject.getValue();
        const updatedTrailers = currentTrailers.map(trailer => 
          trailer.trailer_id === trailerId ? 
          { ...trailer, ...updatedTrailer, ...updatedData } : 
          trailer
        );
        this.trailersSubject.next(updatedTrailers);
        
        this.socketService.handleEntityUpdate({
          type: 'trailer',
          data: updatedTrailers
        });
      })
    );
  }

  /* Updates a trailer's location */
  updateTrailerLocation(trailerId: number, updatedData: Partial<Trailers>): Observable<any> {
    return this.http.put(`${this.apiUrl}/trailers/${trailerId}`, updatedData).pipe(
      tap(() => {
        // Refresh trailers data to update the reactive stream
        this.getAllTrailers().subscribe();
      })
    );
  }

  /* Driver details for drivers page */
  getDriverDetails(): Observable<any> {
    return this.http.get(`${this.apiUrl}/drivers/details`).pipe(
      tap(drivers => {
        this.driversSubject.next(drivers as Drivers[]);
      })
    );
  }

  updateDriverDetails(driverId: number, updatedDetails: Partial<Drivers>): Observable<any> {
    return this.http.put(`${this.apiUrl}/drivers/${driverId}`, updatedDetails).pipe(
      tap(() => {
        this.getDriverDetails().subscribe();
      })
    );
  }

  getDriverSchedule(driverId: number): Observable<any> {
    return this.http.get(`${this.apiUrl}/drivers/${driverId}/schedule`);
  }

  getAllTrucks(): Observable<any> {
    return this.http.get(`${this.apiUrl}/trucks`).pipe(
        tap(trucks => {
            this.trucksSubject.next(trucks as Trucks[]); // Update the trucks subject with the new data
        })
    );
  }

  createTruck(truck: any): Observable<Trucks> {
    return this.http.post<Trucks>(`${this.apiUrl}/trucks`, truck).pipe(
      tap(() => {
        this.getAllTrucks().subscribe();
      })
    );
  }

  updateTruck(truckId: number, updatedData: Partial<Trucks>): Observable<any> {
    return this.http.put(`${this.apiUrl}/trucks/${truckId}`, updatedData);
  }

  removeTruck(truckId: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/trucks/${truckId}`).pipe(
      tap(() => {
        const currentTrucks = this.trucksSubject.getValue();
        const updatedTrucks = currentTrucks.filter(truck => truck.truck_id !== truckId);
        this.trucksSubject.next(updatedTrucks);}))
  }

  /* Helper functions */

  private refreshOrders() {

    this.getOrders().subscribe({
      next: (orders) => {
        this.ordersSubject.next(orders);
      },
      error: (error) => console.error('Error refreshing orders:', error)
    });
  }

  refreshDriverCards() {
    // Get current date from behavior subject
    const currentDate = this.selectedDateSubject.getValue();
    // Refresh the driver cards
    this.getDailyDispatch(currentDate).subscribe(cards => {
      this.driverCardsSubject.next(cards);
    });
  }

  /* Updates selected/required date to display elements on the UI */
  updateDate(date: string) {
    this.selectedDateSubject.next(date);
    this.getDailyDispatch(date).subscribe((driverCards: DriverCard[]) => {
      this.driverCardsSubject.next(driverCards as DriverCard[]); // Updates the state
    });
  }

  /** Sets the current user */
  setUser(user: ApiUser): void {
    this.currentUser.next(user);
  }


  getWeeklyStats(driverId: number, year: number, month: number): Observable<any> {
    return this.http.get(`${this.apiUrl}/analytics/api/dispatch/driver/${driverId}/weekly/${year}/${month}`);
  }

  getMonthlyStats(driverId: number, year: number): Observable<any> {
    return this.http.get(`${this.apiUrl}/analytics/api/dispatch/driver/${driverId}/yearly/${year}`);
  }

  getUniqueYears(): Observable<number[]> {
    return this.http.get<number[]>(`${this.apiUrl}/analytics/api/years`);
  }

  getWeeklyPerformance(driverId: number, year: number, month: number): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/analytics/api/driver/${driverId}/performance/weekly/${year}/${month}`);
  }
  
  getYearlyPerformance(driverId: number, year: number): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/analytics/api/driver/${driverId}/performance/yearly/${year}`);
  }

  getDispatchOrdersForDateRange(driverId: number, start_date: string, end_date: string): Observable<any> {
    const params = new HttpParams()
      .set('start_date', start_date)
      .set('end_date', end_date);

    return this.http.get(`${this.apiUrl}/drivers/${driverId}/dispatches`, { params });
  }

  updateTruckLocation(truckId: number, updatedData: any): Observable<any> {
    return this.http.put(`${this.apiUrl}/trucks/${truckId}`, updatedData);
  }

  generateForm(trailerNumber: string, type: string): Observable<Blob> {
    return this.http.post(`${this.apiUrl}/api/generate-washout-form`, 
      { 
        trailer_number: trailerNumber, 
        type: type
      },
      { responseType: 'blob' }
    );
  }

  getAllCustomers(): Observable<any> {
    return this.http.get(`${this.apiUrl}/customers`).pipe(
      tap(customers => {
        this.customersSubject.next(customers as Customers[]);
      })
    );
  }

  createCustomer(customer: CustomerCreate): Observable<CustomerResponse> {
    return this.http.post<CustomerResponse>(`${this.apiUrl}/customers`, customer);
  }

  createLocation(location: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/locations`, location);
  }

  updateCustomer(customerId: number, customer: any): Observable<CustomerResponse> {
    return this.http.put<CustomerResponse>(`${this.apiUrl}/customers/${customerId}`, customer);
  }

  deleteCustomer(customerId: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/customers/${customerId}`);
  }

  getCustomerLoadDetails(customerId: number): Observable<any> {
    return this.http.get(`${this.apiUrl}/customers/${customerId}/stats`);
  }

  getCustomerLoads(customerId: number): Observable<any> {
    return this.http.get(`${this.apiUrl}/customers/${customerId}/loads`);
  }

  getCustomerChartData(customerId: number, timeRange: string): Observable<any> {
    const params = new HttpParams().set('time_range', timeRange);
    return this.http.get(`${this.apiUrl}/customers/${customerId}/chart-data`, { params });
  }

  getDriverCardsObservable() {
    // Return a merged observable that combines both sources
    return this.driverCardsSubject.asObservable();
  }

  refreshDriverCardsFromSocket(cards: DriverCard[]) {
    // Force a new array reference to trigger change detection
    this.driverCardsSubject.next([...cards]);
    
    // Also refresh the UI by calling the HTTP endpoint
    const currentDate = this.selectedDateSubject.getValue();
    this.getDailyDispatch(currentDate).subscribe({
      next: (freshCards) => {
        this.driverCardsSubject.next(freshCards);
      },
      error: (err) => console.error('Error refreshing cards:', err)
    });
  }

  // Add the getDispatchOrdersObservable method
  getDispatchOrdersObservable(): Observable<any[]> {
    return this.dispatchOrders$;
  }

  // Add a method to expose the SocketService's onMiscTaskDeleted method
  onMiscTaskDeleted(): Observable<any> {
    return this.socketService.onMiscTaskDeleted();
  }

  sendEmail(emailData: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/api/email/send`, emailData);
  }

  getLocations(): Observable<any> {
    return this.http.get(`${this.apiUrl}/locations`)
  }

  // Get locations filtered by type for salt trailers (SALT or BOTH)
  getSaltLocations(): Observable<any> {
    return this.getLocations().pipe(
      map((locations: any[]) => 
        locations.filter(location => 
          location.location_type === 'SALT' || location.location_type === 'BOTH'
        )
      )
    );
  }

  // Get locations filtered by type for van trailers (VAN or BOTH) 
  getVanLocations(): Observable<any> {
    return this.getLocations().pipe(
      map((locations: any[]) => 
        locations.filter(location => 
          location.location_type === 'VAN' || location.location_type === 'BOTH'
        )
      )
    );
  }

  getTenders(): Observable<any> {
    console.log('Getting tenders...');
    return this.http.get(`${this.apiUrl}/tenders`);
  }
}


